import { Credibility } from '@chegg-wt/core/types/core'

const sourcePageCredibilityHeader = 'Source and Citing FAQs'
const findPageCredibilityHeader =
  "Is your source credible? Don't forget to consider these factors:"
const resultsPageCredibilityHeader = ''

export const sourcePageCredibility: Credibility = {
  header: sourcePageCredibilityHeader,
  body: [
    {
      body: [
        "If you're trying to determine what source to choose or what you should cite, read on for FAQs and helpful answers.",
      ],
      isList: false,
    },
    {
      header:
        'I\'m citing a book, article, video, photo, etc., that I found online. Does that mean it\'s a "website"?',
      body: [
        'If you can classify your source as something other than a website/web page, choose that as your source. Be as specific as possible. Most times, the source citation form will give you the option to cite the source as something found online (see tabs at the top of the citation form).',
      ],
      isList: false,
    },
    {
      header: 'Examples',
      body: [
        'E-book -- choose "Book"',
        'Online newspaper article -- choose "Newspaper"',
        'Digital photo -- choose "Photo"',
      ],
    },
    {
      header: 'What\'s the difference between an "Online database" and a "Journal"?',
      body: [
        'In research, a journal is a scholarly or academic periodical featuring articles written by experts. These articles are reviewed by fellow experts (peer-reviewed) before being published.',
        'An online database is an electronic collection of information. They are searchable and most databases found at your library provide credible, published content. Depending on the database, it might also let you access information in various formats (e.g., journals, videos, books, newspapers, etc.).',
        'This means an online database could have several journals.',
      ],
      isList: false,
    },
    {
      header: 'Examples',
      body: [
        'Journals -- Journal of the American Medical Association (JAMA), PLOS One, New Scientist, Ecology Letters',
        'Online databases --- Academic One File, Britannica Academic, EBSCOHost, Facts on File ',
      ],
    },
    {
      header: "I'm still not sure what source I have. What should I do?",
      body: [
        'Scroll through our long list of source options and make your best educated guess. If you\'re still unsure, choose "Miscellaneous." ',
      ],
      isList: false,
    },
    {
      header: 'If there is no author, can I still cite a source?',
      body: [
        "Yes! It's always better to cite a source, even if you're unsure of all the source details. Also, not everything has an indicated author so it's ok to leave an author out in those cases. When this happens, most citation styles will list the source by its title instead of the author's last name.",
      ],
      isList: false,
    },
    {
      header: 'I only need to cite one source, right?',
      body: [
        "A well-balanced paper usually cites several sources; often in different formats (e.g., books, journals, interviews, etc.). There isn't an exact number of sources that is ideal, but try to have more than a couple sources listed.",
        "Also, you should cite everything you've consulted or mentioned in your paper. It's the ethical thing to do.",
      ],
      isList: false,
    },
    {
      header:
        'If I have a full citation at the end of my paper, do I really need to make in-text citations (e.g., parenthetical citations, footnotes, etc.)?',
      body: [
        'Yes, absolutely! Showing where you got certain ideas or points in your paper will help support any arguments you make. Including in-text citations is also ethical — give credit where it is due.',
      ],
      isList: false,
    },
    {
      header: 'I heard that "common knowledge" does NOT need to be cited. What is it?',
      body: [
        'Common knowledge is general information that you can assume a normal individual would know without needing to consult a source. Yes, you do not necessarily need to cite common knowledge. However, if you are unsure if you should cite a fact or source, err on the side of caution and cite it.',
      ],
      isList: false,
    },
    {
      header: 'Examples',
      body: [
        'London is the capital of England',
        'A penguin is a bird',
        'The moon orbits the Earth',
        'Water freezes at 0 degrees Celsius',
      ],
    },
  ],
}

export const findPageCredibility: Credibility = {
  header: findPageCredibilityHeader,
  body: [
    {
      header: 'Purpose : Reason the source exists',
      body: [
        'Is the point of the information to inform, persuade, teach, or sell?',
        'Do the authors/publishers make their intentions clear?',
        'Does the information appear to be fact or opinion?',
        'Does the point of view seem impartial? Do they identify counter-arguments?',
      ],
    },
    {
      header: 'Authority - Author:Source of the information',
      body: [
        'Who is the author? What are their credentials or qualifications?',
        'What makes the author qualified to write on this topic?',
        'Are there clearly defined contact information for the author?',
      ],
    },
    {
      header: 'Authority - Publisher:Source of the information',
      body: [
        'Who is the publisher? Is it a non-profit, government agency, or organisation? How might this affect their point of view?',
        'What makes the publisher qualified to generate works on this subject?',
        'What can the URL tell you about the publisher? For instance, .gov may signify that it is a government agency.',
      ],
    },
    {
      header: 'Accuracy : Reliability and truthfulness of the content',
      body: [
        'Where does the information come from?',
        'Can the information presented be verified? Is it supported by evidence that is clearly cited?',
        'Does the language used seem free of emotion, and does the work seem impartial and objective?',
        'Are there any spelling or grammatical errors? If an online source, are all links working?',
        'If it was reproduced, who edited/reproduced it? Where was the information originally published?',
        'How original are the ideas presented in the work? Do they seem to be common knowledge?',
      ],
    },
    {
      header: 'Relevance : Importance of the information to your topic',
      body: [
        'Does the information relate to your topic, or answer the question you have presented?',
        'Who is the intended audience of the work? Does that audience match with yours?',
        'Have you looked at other sources related to this one? Does it seem there are many others on the topic?',
        'Are you utilizing the entire source, or just a part of it?',
      ],
    },
    {
      header: 'Currency : Timeliness of the information',
      body: [
        'When was the information published? When was it last updated? Does it reflect the most current information available?',
        'How does your topic fit in with this source’s publication date? Do you need current information to make your point or do older sources work better?',
      ],
    },
    {
      header: 'Comprehensiveness',
      body: [
        'Does the source present one or multiple viewpoints on your topic?',
        'Does the source present a large amount of information on the topic? Or is it short and focused?',
        'Are there any points you feel may have been left out, on purpose or accidentally, that affect its comprehensiveness?',
      ],
    },
  ],
}
export const resultsPageCredibility: Credibility = {
  header: resultsPageCredibilityHeader,
  body: [
    {
      header: 'What is this?',
      body: [
        'To help you save time and effort when citing websites, books, journal articles, and other select source types, the Cite This For Me citation tool automatically searches for reference information based on the details you previously provided. That could be the source’s title, author, editor, URL, DOI number, ISBN, or even a keyword.',
        'The results above are all the sources found using the search information you inputted and source type you selected. If you don’t see your source, try going back a step and refining your search query.',
        'If you need help remembering what your search query was, look above the line at the line that says “Results for…”',
      ],
      isList: false,
    },
    {
      header: 'What do I do with these results?',
      body: [
        'Review each entry and click the “Cite” button next to the source you want to cite. Sometimes, there will only be one result to choose. Other times, you’ll see many results. In these cases, examine the extra information shown. Depending on your source type, look carefully for the correct:',
      ],
      isList: false,
    },
    {
      header: '',
      body: [
        'Title of the source',
        'Title of the periodical source (e.g., journal title, newspaper title, etc.)',
        'Author(s)',
        'Year published',
        'Studio or distributor',
      ],
    },
    {
      header: 'What happens next?',
      body: [
        'Once you select your source, you’ll be taken to a confirmation page to review what notable information was and was not found. After that, you will be taken directly to a citation form. Any notable information that was found will already be automatically inputted into the form.',
        'The citation form is editable! You can confirm and leave the auto-inputted information as is, edit what’s there, and add any additional details before adding the reference to your list—you’re always in control and have the final say on your references.',
        'Once you’re done with the form, click “Complete Citation” and the citation will be formatted and added to your bibliography (in the right order). It’s easy, efficient, and a smart way to create references and citations.',
      ],
      isList: false,
    },
    {
      header: 'No sources were found for my search. What do I do?',
      body: [
        'Fear not! You still have tools at your fingertips! Each source type has an easy-to-use citation form. Click the button “Create a Manual Citation” to go directly to the form.',
        'Each blank is clearly labelled and any space with a star is information that you’ll probably need. Once you input everything, click “Create Citation” and the Cite This For Me form will automatically generate a formatted citation! It’s that easy.',
      ],
      isList: false,
    },
  ],
}
